// Notificações do seller (croqui `screens/seller/notificacoes.tsx`, task WEB-66). Zenith
// (WEB-115).
//
// Dois blocos, na ordem do croqui: os dispositivos que recebem push e as preferências por
// evento. O cadastro de dispositivo continua fora deste corte (`spec/17` §8.4): depende de
// service worker e PWA (`spec/11` §4.3), e o aviso do topo diz isso em vez de mostrar um
// botão morto. Nada aqui registra service worker nem consome
// `GET /v1/me/push/vapid-public-key`.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { errorMessageOf } from '../api/errors.ts';
import { ME_QUERY_PREFIX, profileQueryOptions, updateMe } from '../api/queries/account.ts';
import {
  PUSH_SUBSCRIPTIONS_QUERY_KEY,
  type PushSubscription,
  pushSubscriptionsQueryOptions,
  removeSubscription,
  setSubscriptionMuted,
} from '../api/queries/security.ts';
import { useMode } from '../app/mode.tsx';
import { Notice } from '../components/app/notice.tsx';
import { PageHeader } from '../components/app/page-header.tsx';
import { DevicesTable } from '../features/notifications/devices-table.tsx';
import type { NotificationPrefKey } from '../features/notifications/model.ts';
import { PrefsPanel } from '../features/notifications/prefs-panel.tsx';

function Notifications() {
  const { mode } = useMode();
  const queryClient = useQueryClient();
  const devices = useQuery(pushSubscriptionsQueryOptions(mode));
  const profile = useQuery(profileQueryOptions(mode));

  const [deviceError, setDeviceError] = useState<string | undefined>(undefined);
  const [busyDeviceId, setBusyDeviceId] = useState<string | undefined>(undefined);
  const [prefsError, setPrefsError] = useState<string | undefined>(undefined);
  const [busyPref, setBusyPref] = useState<NotificationPrefKey | undefined>(undefined);

  const refreshDevices = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: [PUSH_SUBSCRIPTIONS_QUERY_KEY] });
  };

  const toggleDevice = useMutation({
    mutationFn: async (input: { device: PushSubscription; muted: boolean }) => {
      setDeviceError(undefined);
      setBusyDeviceId(input.device.id);
      return setSubscriptionMuted(mode, input.device.id, input.muted);
    },
    onError: (error) => {
      setDeviceError(errorMessageOf(error));
    },
    onSettled: async () => {
      setBusyDeviceId(undefined);
      await refreshDevices();
    },
  });

  const removeDevice = useMutation({
    mutationFn: async (device: PushSubscription) => {
      setDeviceError(undefined);
      setBusyDeviceId(device.id);
      return removeSubscription(mode, device.id);
    },
    onError: (error) => {
      setDeviceError(errorMessageOf(error));
    },
    onSettled: async () => {
      setBusyDeviceId(undefined);
      await refreshDevices();
    },
  });

  /**
   * `PATCH /v1/me` com merge parcial no servidor (API-24, "Questões abertas" 4): mandar
   * uma chave não religa as outras. A resposta é o perfil inteiro, então ela entra no
   * cache deste mode; o outro mode é invalidado porque a preferência NÃO é mode-scoped e
   * ficaria velha lá.
   */
  const togglePref = useMutation({
    mutationFn: async (input: { key: NotificationPrefKey; value: boolean }) => {
      setPrefsError(undefined);
      setBusyPref(input.key);
      return updateMe(mode, { notification_prefs: { [input.key]: input.value } });
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(profileQueryOptions(mode).queryKey, updated);
      void queryClient.invalidateQueries({ queryKey: [ME_QUERY_PREFIX] });
    },
    onError: (error) => {
      setPrefsError(errorMessageOf(error));
    },
    onSettled: () => {
      setBusyPref(undefined);
    },
  });

  const rows = devices.data ?? [];

  return (
    <>
      <PageHeader
        title="Notificações"
        description="Dispositivos com push ativado e preferências por evento"
      />

      <Notice
        tone="info"
        title="Cadastrar um novo dispositivo ainda não está disponível por aqui."
        sub="Essa etapa depende de um app instalável (PWA) e fica para uma onda futura, por enquanto dá pra gerenciar os já cadastrados."
      />

      <DevicesTable
        devices={rows}
        isLoading={devices.isPending}
        // Erro só quando não há lista: com o polling de 30 s, um flake num refetch de
        // fundo trocaria a lista carregada por uma tela de falha.
        error={devices.data === undefined ? devices.error : undefined}
        onRetry={() => {
          void devices.refetch();
        }}
        onToggleMuted={(device, muted) => {
          toggleDevice.mutate({ device, muted });
        }}
        onRemove={(device) => {
          removeDevice.mutate(device);
        }}
        {...(busyDeviceId === undefined ? {} : { busyDeviceId })}
        {...(deviceError === undefined ? {} : { errorMessage: deviceError })}
      />

      <PrefsPanel
        {...(profile.data === undefined ? {} : { prefs: profile.data.notification_prefs })}
        isLoading={profile.isPending}
        error={profile.data === undefined ? profile.error : undefined}
        onRetry={() => {
          void profile.refetch();
        }}
        onToggle={(key, value) => {
          togglePref.mutate({ key, value });
        }}
        {...(busyPref === undefined ? {} : { busyKey: busyPref })}
        {...(prefsError === undefined ? {} : { errorMessage: prefsError })}
      />
    </>
  );
}

export const Route = createFileRoute('/app/settings/notifications/')({ component: Notifications });
